import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';

const API = `${API_V1}/insurance`;
const TABS = ['Policies', 'Claims', 'Premium Schedule'];
const POLICY_TYPES = ['PROPERTY', 'GENERAL_LIABILITY', 'PROFESSIONAL_LIABILITY', 'D_AND_O', 'CYBER', 'AUTO', 'WORKERS_COMP', 'UMBRELLA', 'KEY_PERSON', 'BUSINESS_INTERRUPTION'];
const FREQUENCIES = ['ANNUAL', 'SEMI_ANNUAL', 'QUARTERLY', 'MONTHLY'];
const CLAIM_STATUSES = ['FILED', 'UNDER_REVIEW', 'APPROVED', 'SETTLED', 'DENIED'];

const emptyForm = {
  policy_type: 'GENERAL_LIABILITY', insurer_name: '', broker_name: '',
  coverage_amount: '', deductible: '0', annual_premium: '',
  effective_date: '', expiration_date: '', payment_frequency: 'ANNUAL',
  currency: 'USD', entity_code: '', notes: '',
};

export default function InsuranceManagement() {
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('Policies');
  const [summary, setSummary] = useState<any>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Policies
  const [policies, setPolicies] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedData, setExpandedData] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [glPreview, setGlPreview] = useState<any>(null);

  // Claims
  const [claims, setClaims] = useState<any[]>([]);
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  const [showFileClaim, setShowFileClaim] = useState(false);
  const [claimPolicyId, setClaimPolicyId] = useState<number | null>(null);
  const [claimForm, setClaimForm] = useState({ claim_amount: '', incident_date: '', description: '', adjuster_name: '' });

  // Premium schedule
  const [premiums, setPremiums] = useState<any[]>([]);

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const loadSummary = useCallback(async () => {
    try { const r = await authFetch(withEntityParam(`${API}/summary`, entity)); if (r.ok) setSummary(await r.json()); } catch (e) { console.error(e); }
  }, [entity]);
  const loadPolicies = useCallback(async () => {
    const p = new URLSearchParams({ limit: '50' }); if (statusFilter) p.set('status', statusFilter);
    try { const r = await authFetch(withEntityParam(`${API}?${p}`, entity)); if (r.ok) { const d = await r.json(); setPolicies(d.items || []); } } catch (e) { console.error(e); }
  }, [statusFilter, entity]);
  const loadClaims = useCallback(async () => {
    const p = new URLSearchParams({ limit: '50' }); if (claimStatusFilter) p.set('status', claimStatusFilter);
    try { const r = await authFetch(withEntityParam(`${API}/claims?${p}`, entity)); if (r.ok) { const d = await r.json(); setClaims(d.items || []); } } catch (e) { console.error(e); }
  }, [claimStatusFilter, entity]);
  const loadPremiums = useCallback(async () => {
    try { const r = await authFetch(withEntityParam(`${API}/premium-schedule?limit=60`, entity)); if (r.ok) setPremiums(await r.json()); } catch (e) { console.error(e); }
  }, [entity]);

  useEffect(() => { loadSummary(); loadPolicies(); }, [loadSummary, loadPolicies]);
  useEffect(() => { if (tab === 'Claims') loadClaims(); }, [tab, loadClaims]);
  useEffect(() => { if (tab === 'Premium Schedule') loadPremiums(); }, [tab, loadPremiums]);

  // Load GL preview when create modal opens or entity changes
  useEffect(() => {
    if (!showCreate) return;
    const p = new URLSearchParams();
    if (form.entity_code) p.set('entity_code', form.entity_code);
    authFetch(`${API}/preview-gl?${p}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setGlPreview(d))
      .catch(() => setGlPreview(null));
  }, [showCreate, form.entity_code]);

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    try { const r = await authFetch(`${API}/${id}`); if (r.ok) { setExpandedData(await r.json()); setExpandedId(id); } } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    // Frontend validation — fail fast before sending
    if (!form.coverage_amount || parseFloat(form.coverage_amount) <= 0) {
      showToast('Coverage Amount is required and must be > 0', false); return;
    }
    if (!form.annual_premium || parseFloat(form.annual_premium) <= 0) {
      showToast('Annual Premium is required and must be > 0', false); return;
    }
    if (!form.effective_date) { showToast('Effective Date is required', false); return; }
    if (!form.expiration_date) { showToast('Expiration Date is required', false); return; }
    if (form.expiration_date <= form.effective_date) {
      showToast('Expiration Date must be after Effective Date', false); return;
    }

    try {
      const body: any = { ...form };
      body.coverage_amount = parseFloat(body.coverage_amount);
      body.deductible = parseFloat(body.deductible) || 0;
      body.annual_premium = parseFloat(body.annual_premium);
      if (!body.entity_code) delete body.entity_code;
      if (!body.insurer_name) delete body.insurer_name;
      if (!body.broker_name) delete body.broker_name;
      if (!body.notes) delete body.notes;
      const r = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) {
        const d = await r.json();
        showToast(`Policy ${d.policy_number} created in DRAFT. GL: ${d.gl_resolution_method || 'N/A'} (${d.gl_resolution_confidence || 0}%)`);
        setShowCreate(false);
        setForm({ ...emptyForm });
        loadPolicies();
        loadSummary();
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(`Create failed: ${e.detail || `HTTP ${r.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network error'}`, false);
    }
  };

  const handleSubmitForApproval = async (id: number) => {
    try {
      const r = await authFetch(`${API}/${id}/submit-for-approval`, { method: 'POST' });
      if (r.ok) { showToast('Submitted for approval'); loadPolicies(); loadSummary(); setExpandedId(null); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleAction = async (id: number, action: string, body: any = {}) => {
    try {
      const r = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { showToast(`${action} completed`); loadPolicies(); loadSummary(); setExpandedId(null); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleFileClaim = async () => {
    if (!claimPolicyId) return;
    try {
      const body: any = { claim_amount: parseFloat(claimForm.claim_amount) };
      if (claimForm.incident_date) body.incident_date = claimForm.incident_date;
      if (claimForm.description) body.description = claimForm.description;
      if (claimForm.adjuster_name) body.adjuster_name = claimForm.adjuster_name;
      const r = await authFetch(`${API}/${claimPolicyId}/claims`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { showToast('Claim filed'); setShowFileClaim(false); loadClaims(); loadSummary(); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleClaimAction = async (claimId: number, status: string) => {
    try {
      const r = await authFetch(`${API}/claims/${claimId}/status`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      if (r.ok) { showToast(`Claim ${status.toLowerCase()}`); loadClaims(); loadSummary(); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleAccrue = async (policyId: number, period: string) => {
    try {
      const r = await authFetch(`${API}/${policyId}/accrue/${period}`, { method: 'POST' });
      if (r.ok) { showToast('Premium posted'); toggleExpand(policyId); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, marginBottom: 2 };
  const thStyle = { padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, textAlign: 'left' as const, borderBottom: '2px solid var(--border-subtle)' };
  const tdStyle = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--border-subtle)' };
  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 };

  return (
    <PageLayout title="Insurance Management" subtitle="Policies, claims, premium schedules, and coverage analysis"
      actions={[{ label: '+ New Policy', onClick: () => setShowCreate(true) }]}>
      {toast && (<div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '10px 20px', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, maxWidth: 500, background: toast.ok ? '#16a34a' : '#ef4444' }}>{toast.msg}</div>)}

      {summary && (<KpiGrid items={[
        { label: 'Total Coverage', value: formatAmountCompact(summary.total_coverage) },
        { label: 'Annual Premium', value: formatAmountCompact(summary.total_annual_premium) },
        { label: 'Active Policies', value: String(summary.active_policies) },
        { label: 'Expiring Soon', value: String(summary.expiring_soon), color: summary.expiring_soon > 0 ? 'var(--warning)' : undefined },
        { label: 'Open Claims', value: String(summary.open_claims), color: summary.open_claims > 0 ? 'var(--negative)' : undefined },
        { label: 'Claims Outstanding', value: formatAmountCompact(summary.claims_outstanding) },
      ]} />)}

      <div style={{ marginTop: 16 }}><TabBar tabs={TABS.map(t => ({ key: t, label: t }))} active={tab} onChange={v => setTab(v)} /></div>

      {/* ═══ TAB: Policies ═══ */}
      {tab === 'Policies' && (<>
        <FilterBar filters={[{ key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: (v: string) => setStatusFilter(v), options: ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'EXPIRED', 'RENEWED', 'CANCELLED'].map(s => ({ value: s, label: s })) }]} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Policy #', 'Type', 'Insurer', 'Coverage', 'Premium', 'Effective', 'Expires', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {policies.length === 0 ? (<tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No policies yet.</td></tr>) :
            policies.map(p => (<>
              <tr key={p.id} onClick={() => toggleExpand(p.id)} style={{ cursor: 'pointer', background: expandedId === p.id ? 'var(--accent-subtle)' : 'transparent' }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{p.policy_number}</td>
                <td style={tdStyle}>{p.policy_type.replace(/_/g, ' ')}</td>
                <td style={tdStyle}>{p.insurer_name || '-'}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(p.coverage_amount)}</td>
                <td style={tdStyle}>{formatAmount(p.annual_premium)}</td>
                <td style={tdStyle}>{p.effective_date || '-'}</td>
                <td style={tdStyle}>{p.expiration_date || '-'}</td>
                <td style={tdStyle}><StatusBadge status={p.lifecycle_status} /></td>
                <td style={tdStyle} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {p.lifecycle_status === 'DRAFT' && <ActionButton label="Submit" size="sm" onClick={() => handleSubmitForApproval(p.id)} />}
                    {p.lifecycle_status === 'PENDING_APPROVAL' && <ActionButton label="Approve & Activate" size="sm" variant="success" onClick={() => handleAction(p.id, 'activate')} />}
                    {(p.lifecycle_status === 'ACTIVE' || p.lifecycle_status === 'EXPIRED') && <ActionButton label="Renew" size="sm" variant="secondary" onClick={() => { const exp = prompt('New expiration (YYYY-MM-DD):'); if (exp) handleAction(p.id, 'renew', { new_expiration: exp }); }} />}
                    {p.lifecycle_status === 'ACTIVE' && <ActionButton label="File Claim" size="sm" variant="danger" onClick={() => { setClaimPolicyId(p.id); setClaimForm({ claim_amount: '', incident_date: '', description: '', adjuster_name: '' }); setShowFileClaim(true); }} />}
                  </div>
                </td>
              </tr>
              {expandedId === p.id && expandedData && (
                <tr key={`${p.id}-d`}><td colSpan={9} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                      {[{ l: 'Deductible', v: formatAmount(expandedData.deductible) }, { l: 'Broker', v: expandedData.broker_name || 'N/A' }, { l: 'Payment Freq', v: expandedData.payment_frequency }, { l: 'Currency', v: expandedData.currency }, { l: 'Renewal Date', v: expandedData.renewal_date || 'None' }, { l: 'Entity', v: expandedData.entity_code || 'All' }].map(d => (
                        <div key={d.l} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6 }}><div style={lbl}>{d.l}</div><div style={{ fontSize: 12 }}>{d.v}</div></div>
                      ))}
                    </div>
                    <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>GL Accounts</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                        {[{ l: 'Prepaid Insurance', v: expandedData.prepaid_insurance_account }, { l: 'Insurance Expense', v: expandedData.insurance_expense_account }, { l: 'Claims Receivable', v: expandedData.claims_receivable_account }, { l: 'Cash', v: expandedData.cash_account }].map(d => (
                          <div key={d.l}><div style={lbl}>{d.l}</div><div style={{ fontSize: 12, fontFamily: 'monospace', color: d.v ? 'var(--accent)' : 'var(--text-hint)' }}>{d.v || 'Not resolved'}</div></div>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4 }}>Method: {expandedData.gl_resolution_method || 'N/A'} | Confidence: {expandedData.gl_resolution_confidence ?? 'N/A'}%</div>
                    </div>
                    {expandedData.premium_schedule?.length > 0 && (<div>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>PREMIUM SCHEDULE ({expandedData.premium_schedule.length})</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead><tr>{['Period', 'Due Date', 'Amount', 'Status', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>{h}</th>)}</tr></thead>
                        <tbody>{expandedData.premium_schedule.map((s: any) => (
                          <tr key={s.period} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '4px 8px' }}>{s.period}</td>
                            <td style={{ padding: '4px 8px' }}>{s.due_date || '-'}</td>
                            <td style={{ padding: '4px 8px', fontWeight: 600 }}>{formatAmount(s.premium_amount)}</td>
                            <td style={{ padding: '4px 8px' }}><StatusBadge status={s.status} size="sm" /></td>
                            <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                              {s.status === 'SCHEDULED' && expandedData.lifecycle_status === 'ACTIVE' && <ActionButton label="Pay" size="sm" variant="success" onClick={() => handleAccrue(expandedData.id, s.period)} />}
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>)}
                    {expandedData.claims?.length > 0 && (<div>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>CLAIMS ({expandedData.claims.length})</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead><tr>{['Claim #', 'Date', 'Amount', 'Deductible', 'Recovery', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>{h}</th>)}</tr></thead>
                        <tbody>{expandedData.claims.map((c: any) => (
                          <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '4px 8px', fontWeight: 600 }}>{c.claim_number}</td>
                            <td style={{ padding: '4px 8px' }}>{c.claim_date}</td>
                            <td style={{ padding: '4px 8px' }}>{formatAmount(c.claim_amount)}</td>
                            <td style={{ padding: '4px 8px' }}>{formatAmount(c.deductible_amount)}</td>
                            <td style={{ padding: '4px 8px', fontWeight: 600 }}>{formatAmount(c.recovery_amount)}</td>
                            <td style={{ padding: '4px 8px' }}><StatusBadge status={c.lifecycle_status} size="sm" /></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>)}
                  </div>
                </td></tr>
              )}
            </>))}
          </tbody>
        </table>
      </>)}

      {/* ═══ TAB: Claims ═══ */}
      {tab === 'Claims' && (<>
        <FilterBar filters={[{ key: 'status', label: 'Status', type: 'select', value: claimStatusFilter, onChange: (v: string) => setClaimStatusFilter(v), options: CLAIM_STATUSES.map(s => ({ value: s, label: s })) }]} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Claim #', 'Policy', 'Date', 'Amount', 'Deductible', 'Recovery', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {claims.length === 0 ? (<tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No claims.</td></tr>) :
            claims.map(c => (
              <tr key={c.id}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{c.claim_number}</td>
                <td style={tdStyle}>{c.policy_number || '-'}</td>
                <td style={tdStyle}>{c.claim_date}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(c.claim_amount)}</td>
                <td style={tdStyle}>{formatAmount(c.deductible_amount)}</td>
                <td style={tdStyle}>{formatAmount(c.recovery_amount)}</td>
                <td style={tdStyle}><StatusBadge status={c.lifecycle_status} /></td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {c.lifecycle_status === 'FILED' && <ActionButton label="Review" size="sm" onClick={() => handleClaimAction(c.id, 'UNDER_REVIEW')} />}
                    {c.lifecycle_status === 'UNDER_REVIEW' && <><ActionButton label="Approve" size="sm" variant="success" onClick={() => handleClaimAction(c.id, 'APPROVED')} /><ActionButton label="Deny" size="sm" variant="danger" onClick={() => handleClaimAction(c.id, 'DENIED')} /></>}
                    {c.lifecycle_status === 'APPROVED' && <ActionButton label="Settle" size="sm" variant="success" onClick={() => handleClaimAction(c.id, 'SETTLED')} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>)}

      {/* ═══ TAB: Premium Schedule ═══ */}
      {tab === 'Premium Schedule' && (
        <div style={{ marginTop: 12 }}>
          {premiums.length === 0 ? (<div style={{ textAlign: 'center', color: 'var(--text-hint)', padding: 32 }}>No scheduled premiums.</div>) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Period', 'Total Premium', 'Policies'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>{premiums.map(s => (
                <tr key={s.period}><td style={{ ...tdStyle, fontWeight: 600 }}>{s.period}</td><td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(s.total_premium)}</td><td style={tdStyle}>{s.policy_count}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* ═══ Create Policy Modal ═══ */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Insurance Policy" size="lg"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowCreate(false)} /><ActionButton label="Create" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Policy Type</div><select value={form.policy_type} onChange={e => setForm({ ...form, policy_type: e.target.value })} style={inputStyle}>{POLICY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</select></div>
            <div><div style={lbl}>Insurer</div><input value={form.insurer_name} onChange={e => setForm({ ...form, insurer_name: e.target.value })} placeholder="Insurance company" style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Coverage Amount *</div><input type="number" value={form.coverage_amount} onChange={e => setForm({ ...form, coverage_amount: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Deductible</div><input type="number" value={form.deductible} onChange={e => setForm({ ...form, deductible: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Annual Premium *</div><input type="number" value={form.annual_premium} onChange={e => setForm({ ...form, annual_premium: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Effective Date *</div><input type="date" value={form.effective_date} onChange={e => setForm({ ...form, effective_date: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Expiration Date *</div><input type="date" value={form.expiration_date} onChange={e => setForm({ ...form, expiration_date: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Payment Frequency</div><select value={form.payment_frequency} onChange={e => setForm({ ...form, payment_frequency: e.target.value })} style={inputStyle}>{FREQUENCIES.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}</select></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Entity *</div>
              <select aria-label="Entity" value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={inputStyle}>
                <option value="">Select entity...</option>
                {entities.map(en => (
                  <option key={en.entity_code} value={en.entity_code}>
                    {en.entity_code} — {en.entity_name} ({en.functional_currency})
                  </option>
                ))}
              </select>
            </div>
            <div><div style={lbl}>Broker</div><input value={form.broker_name} onChange={e => setForm({ ...form, broker_name: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Currency</div><input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={inputStyle} /></div>
          </div>
          <div><div style={lbl}>Notes</div><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>

          {/* GL Resolution Preview */}
          {glPreview && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-subtle)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                GL Accounts (auto-resolved from your CoA — {glPreview.gl_resolution_method || 'N/A'}, {glPreview.gl_resolution_confidence || 0}% confidence)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { l: 'Prepaid Insurance', code: glPreview.prepaid_insurance_account },
                  { l: 'Insurance Expense', code: glPreview.insurance_expense_account },
                  { l: 'Claims Receivable', code: glPreview.claims_receivable_account },
                  { l: 'Cash / Bank', code: glPreview.cash_account },
                ].map(d => (
                  <div key={d.l} style={{ fontSize: 11 }}>
                    <span style={{ color: 'var(--text-hint)' }}>{d.l}: </span>
                    {d.code ? (
                      <>
                        <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{d.code}</span>
                        {glPreview.account_names?.[d.code] && (
                          <span style={{ color: 'var(--text-secondary)' }}> — {glPreview.account_names[d.code]}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--negative)' }}>NOT RESOLVED — upload CoA or add manually</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 6, fontStyle: 'italic' }}>
                Note: insurance contract upload via Contract Intelligence is the recommended entry point.
                You can edit GL accounts on the policy detail after creation.
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ═══ File Claim Modal ═══ */}
      <Modal open={showFileClaim} onClose={() => setShowFileClaim(false)} title="File Insurance Claim" size="md"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowFileClaim(false)} /><ActionButton label="File Claim" variant="danger" onClick={handleFileClaim} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Claim Amount *</div><input type="number" value={claimForm.claim_amount} onChange={e => setClaimForm({ ...claimForm, claim_amount: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Incident Date</div><input type="date" value={claimForm.incident_date} onChange={e => setClaimForm({ ...claimForm, incident_date: e.target.value })} style={inputStyle} /></div>
          </div>
          <div><div style={lbl}>Description</div><textarea value={claimForm.description} onChange={e => setClaimForm({ ...claimForm, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
          <div><div style={lbl}>Adjuster Name</div><input value={claimForm.adjuster_name} onChange={e => setClaimForm({ ...claimForm, adjuster_name: e.target.value })} style={inputStyle} /></div>
        </div>
      </Modal>
    </PageLayout>
  );
}
